//@ts-check
import { ChevronLeft, ChevronRight, LogOut } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';

import { Button } from '@/components/ui/button.jsx';
import { useAuth } from '@/features/auth/AuthContext.jsx';
import { useBranding } from '@/hooks/useBranding.js';
import { getVisibleNav } from '@/lib/nav.js';
import { cn } from '@/lib/utils.js';

/**
 * Collapsible left sidebar with role-based nav.
 * Fixed viewport height: header + footer pinned, only nav scrolls.
 * @param {{ collapsed: boolean, onToggle: () => void }} props
 */
export function Sidebar({ collapsed, onToggle }) {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [logoBroken, setLogoBroken] = useState(false);

  useEffect(() => {
    setLogoBroken(false);
  }, [branding.logoUrl]);

  if (!user) return null;

  const nav = getVisibleNav(user);
  const initial = user.name?.charAt(0)?.toUpperCase() ?? 'U';
  const showLogo = Boolean(branding.logoUrl) && !logoBroken;

  return (
    <aside
      className={cn(
        'flex h-dvh max-h-dvh min-h-0 shrink-0 flex-col overflow-hidden border-r border-border bg-white transition-[width] duration-200',
        collapsed ? 'w-[72px]' : 'w-64',
      )}
    >
      <div className="flex shrink-0 items-center gap-3 border-b border-border px-3 py-3">
        {showLogo ? (
          <img
            src={branding.logoUrl ?? undefined}
            alt=""
            width={36}
            height={36}
            className="h-9 w-9 rounded-md object-contain"
            onError={() => setLogoBroken(true)}
          />
        ) : (
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-sm font-bold text-white">
            V
          </div>
        )}
        {!collapsed ? (
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground">{branding.siteTitle}</p>
            <p className="truncate text-xs text-muted-foreground">{branding.siteSubtitle}</p>
          </div>
        ) : null}
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0 rounded-full"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>

      <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto overscroll-contain px-2 py-2">
        {nav.map((item) => {
          if (item.children?.length) {
            return (
              <div key={item.label}>
                <button
                  type="button"
                  className={cn(
                    'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50',
                    collapsed && 'justify-center px-0',
                  )}
                  onClick={() => setSettingsOpen((v) => !v)}
                >
                  {item.icon ? <item.icon className="h-4 w-4 shrink-0" /> : null}
                  {!collapsed ? <span>{item.label}</span> : null}
                </button>
                {settingsOpen && !collapsed
                  ? item.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to ?? '#'}
                        className={({ isActive }) =>
                          cn(
                            'ml-3 flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium',
                            isActive
                              ? 'bg-primary/10 text-primary'
                              : 'text-slate-600 hover:bg-slate-50',
                          )
                        }
                      >
                        {child.icon ? <child.icon className="h-4 w-4" /> : null}
                        <span>{child.label}</span>
                      </NavLink>
                    ))
                  : null}
              </div>
            );
          }

          return (
            <NavLink
              key={item.to}
              to={item.to ?? '#'}
              title={item.label}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium',
                  collapsed && 'justify-center px-0',
                  isActive ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-50',
                )
              }
            >
              {item.icon ? <item.icon className="h-4 w-4 shrink-0" /> : null}
              {!collapsed ? <span>{item.label}</span> : null}
            </NavLink>
          );
        })}
      </nav>

      <div className="shrink-0 border-t border-border bg-white p-3">
        <div className={cn('mb-2 flex items-center gap-3', collapsed && 'justify-center')}>
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
            {initial}
          </div>
          {!collapsed ? (
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{user.name}</p>
              <p className="truncate text-xs capitalize text-muted-foreground">{user.role}</p>
            </div>
          ) : null}
        </div>
        <Button
          variant="outline"
          className={cn('w-full', collapsed && 'px-0')}
          onClick={() => void logout()}
        >
          <LogOut className="h-4 w-4" />
          {!collapsed ? 'Logout' : null}
        </Button>
      </div>
    </aside>
  );
}
